// Admin: a user's identity / address proofs and business documents, each approved or rejected on
// its own (a rejection needs a note the user sees). For vendors, approving the owner identity
// proof and a business document lets them add their services.
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box, Stack, Typography, Chip, Button, TextField, Alert, Paper, Link,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { REVIEW_IDENTITY_DOCUMENT_MUTATION } from '../graphql/queries';
import { mediaUrl } from '../utils/media';

const PURPOSE = { identity: 'Owner / identity proof', address: 'Address proof', business: 'Business document' };
const DOC = {
  aadhaar: 'Aadhaar', pan: 'PAN card', passport: 'Passport', voter_id: 'Voter ID', driving_licence: 'Driving licence', other: 'Other government ID',
  gst_certificate: 'GST certificate', trade_licence: 'Trade licence', udyam: 'Udyam (MSME) certificate', shop_establishment: 'Shop & Establishment',
  fssai_licence: 'FSSAI licence', business_other: 'Other business registration',
};
const STATUS = { pending: ['Waiting for review', 'warning'], accepted: ['Approved', 'success'], rejected: ['Rejected', 'error'] };
export const ACCOUNT_STATUS = {
  unverified: ['Not verified', 'default'], pending: ['Waiting for review', 'warning'], verified: ['Verified', 'success'], rejected: ['Not accepted', 'error'],
};
const fmt = (iso) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

// v: { userId, name, email, roleName, businessName, status, documents }
export default function DocumentReview({ v, onChanged }) {
  const [notes, setNotes] = useState({});
  const [message, setMessage] = useState(null);
  const [review, { loading }] = useMutation(REVIEW_IDENTITY_DOCUMENT_MUTATION);
  const isVendor = v.roleName === 'vendor';
  const docs = v.documents || [];
  const has = (purpose) => docs.some(d => d.purpose === purpose && d.status === 'accepted');

  const decide = async (d, decision) => {
    setMessage(null);
    try {
      const { data } = await review({ variables: { id: d.id, decision, note: notes[d.id] || null } });
      const status = data.reviewIdentityDocument;
      setMessage({
        severity: decision === 'accepted' ? 'success' : 'info',
        text: status === 'verified'
          ? (isVendor ? 'All documents are approved: the vendor is verified and can now add services. They have been notified.' : 'The account is verified.')
          : decision === 'accepted' ? 'Approved.' : 'Rejected. The user has been told why.',
      });
      setNotes(n => ({ ...n, [d.id]: '' }));
      if (onChanged) onChanged();
    } catch (e) { setMessage({ severity: 'error', text: e.message }); }
  };

  const [statusLabel, statusColor] = ACCOUNT_STATUS[v.status] || ACCOUNT_STATUS.unverified;
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={1}>
        <Chip size="small" label={statusLabel} color={statusColor} />
        {isVendor && (
          <>
            <Chip size="small" variant="outlined" color={has('identity') ? 'success' : 'default'} label={`Owner ID ${has('identity') ? 'approved' : 'not approved yet'}`} />
            <Chip size="small" variant="outlined" color={has('business') ? 'success' : 'default'} label={`Business document ${has('business') ? 'approved' : 'not approved yet'}`} />
          </>
        )}
      </Stack>
      {message && <Alert severity={message.severity} sx={{ mb: 1 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {!docs.length && <Typography variant="body2" color="text.secondary">No documents uploaded yet.</Typography>}
      <Stack spacing={1}>
        {docs.map(d => {
          const [label, color] = STATUS[d.status] || [d.status, 'default'];
          return (
            <Paper key={d.id} variant="outlined" sx={{ p: 1.25 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{PURPOSE[d.purpose] || d.purpose}: {DOC[d.docType] || d.docType}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {d.url
                      ? <Link href={mediaUrl(d.url)} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>{d.fileName}<OpenInNewIcon sx={{ fontSize: 13 }} /></Link>
                      : <Typography variant="caption">{d.fileName}</Typography>}
                    <Typography variant="caption" color="text.secondary">uploaded {fmt(d.createdAt)}</Typography>
                    <Chip size="small" label={label} color={color} variant={d.status === 'pending' ? 'outlined' : 'filled'} sx={{ height: 20 }} />
                  </Stack>
                  {d.note && <Typography variant="caption" color="text.secondary" display="block">Note: {d.note}</Typography>}
                </Box>
                <TextField size="small" placeholder="Note (required to reject)" value={notes[d.id] || ''} sx={{ minWidth: 220 }}
                  onChange={e => setNotes(n => ({ ...n, [d.id]: e.target.value }))} />
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} disabled={loading || d.status === 'accepted'} onClick={() => decide(d, 'accepted')}>Approve</Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />} disabled={loading || d.status === 'rejected' || !(notes[d.id] || '').trim()} onClick={() => decide(d, 'rejected')}>Reject</Button>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        Document links expire after 30 minutes; reopen this to get new ones. Opening a document is recorded in the audit log.
      </Typography>
    </Box>
  );
}
